/**
 * Schedule a report for delivery, or change a schedule that exists.
 *
 * A schedule is saved paused: activating it is a separate, approval-gated
 * action, so nothing starts emailing because a form was submitted. The server
 * also pauses any schedule it is asked to change, and this dialog says so
 * before it saves rather than after.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CalendarClock } from 'lucide-react'
import DialogShell from '@/components/crm/DialogShell'
import { deliveryModeFor, reportSchedulesService, scheduleInput } from '@/services/reportSchedules'
import type { ReportSchedule, ReportScheduleInput, ScheduleFormat, ScheduleFrequency } from '../../pipeline-intelligence/types'
import { REPORT_TEMPLATES, SCHEDULE_TIMEZONES, templateNeedsLoan } from '../../pipeline-intelligence/types'
import { parseTimeInput, timeInputValue } from '../../pipeline-intelligence/scheduleClock'
import { SchedulerStatusNotice } from '../SchedulerStatusNotice'
import { engineTemplateOf } from './catalog'
import type { LibraryReport } from './library'
import { RecipientsEditor } from './RecipientsEditor'
import { Btn, Field, INPUT } from './ui'
import { requestErrorMessage } from './useReportActions'

export type ScheduleTarget =
  | { mode: 'new'; report: LibraryReport }
  | { mode: 'edit'; schedule: ReportSchedule }

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

type Frequency = ScheduleFrequency

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every two weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
]

/** A schedule keeps the cadence it has: only a value the server does not know falls back to weekly. */
function asFrequency(value: string): Frequency {
  return FREQUENCY_OPTIONS.some((option) => option.value === value) ? (value as Frequency) : 'weekly'
}

/** Weekly and every-two-weeks run on a weekday; monthly and quarterly on a day of the month. */
const usesWeekday = (frequency: Frequency) => frequency === 'weekly' || frequency === 'biweekly'
const usesDayOfMonth = (frequency: Frequency) => frequency === 'monthly' || frequency === 'quarterly'


export function ScheduleDialog({
  target,
  senderEmail,
  onClose,
  onSaved,
  onOpenStudio,
}: {
  target: ScheduleTarget
  senderEmail: string | null
  onClose: () => void
  onSaved: (message: string) => Promise<void> | void
  /** For a pipeline report: its design and its version-pinned schedule live in Report Studio. */
  onOpenStudio?: () => void
}) {
  const editing = target.mode === 'edit' ? target.schedule : null
  const report = target.mode === 'new' ? target.report : null
  const template = editing ? editing.template : report!.definition ? engineTemplateOf(report!.definition.reportType) : report!.template
  const baseFilters: Record<string, unknown> = editing
    ? editing.filters
    : (report!.definition?.filters ?? (report!.loanId ? { loan_id: report!.loanId } : {}))

  const [name, setName] = useState(editing?.name ?? report!.name)
  const [frequency, setFrequency] = useState<Frequency>(editing ? asFrequency(editing.frequency) : 'weekly')
  const [weekday, setWeekday] = useState(editing?.dayOfWeek ?? 0)
  const [dayOfMonth, setDayOfMonth] = useState(editing?.dayOfMonth ?? 1)
  const [businessDays, setBusinessDays] = useState(editing?.businessDaysOnly ?? false)
  // HH:MM, local to `timezone`; the API takes it apart into hourUtc + minuteLocal.
  const [time, setTime] = useState(editing ? timeInputValue(editing.hourUtc, editing.minuteLocal) : '12:00')
  const [timezone, setTimezone] = useState(editing?.timezone ?? 'America/New_York')
  const [recipients, setRecipients] = useState<string[]>(editing?.recipients ?? [])
  const [asDigest, setAsDigest] = useState(editing ? editing.format === 'digest' : true)
  const [fileFormat, setFileFormat] = useState<'pdf' | 'xlsx'>(
    editing?.format === 'xlsx' ? 'xlsx' : !editing && report!.runs[0]?.format === 'xlsx' && !report!.definition ? 'xlsx' : 'pdf',
  )
  const [error, setError] = useState<string | null>(null)

  // The mode follows the recipients, but an edit never rewrites a mode the form does not show: with the
  // recipients unchanged the stored mode stands, and a team schedule stays a team schedule while anybody is
  // on it (team and reviewer delivery are gated separately on the server).
  const storedMode = editing && ['none', 'reviewer', 'team'].includes(String(editing.deliveryMode)) ? editing.deliveryMode : null
  const recipientsUnchanged =
    editing !== null &&
    editing.recipients.length === recipients.length &&
    editing.recipients.every((address) => recipients.includes(address))
  const deliveryMode: 'none' | 'reviewer' | 'team' =
    storedMode && recipientsUnchanged
      ? (storedMode as 'none' | 'reviewer' | 'team')
      : storedMode === 'team' && recipients.length > 0
        ? 'team'
        : deliveryModeFor(recipients)
  const templateMeta = REPORT_TEMPLATES.find((entry) => entry.id === template)
  const formats = (templateMeta?.formats ?? ['pdf']) as ScheduleFormat[]
  const digestAvailable = formats.includes('digest')
  const canChooseFile = formats.includes('pdf') && formats.includes('xlsx')
  // A digest is a way of writing the mail, not a third file type, so it is offered beside Delivery
  // rather than as an output format — and only where there is a digest to render and somebody to
  // send it to. Retention-only schedules send no mail, so the choice does not apply to them.
  const useDigest = digestAvailable && deliveryMode !== 'none' && asDigest
  const format: ScheduleFormat = useDigest
    ? 'digest'
    : canChooseFile
      ? fileFormat
      : formats.includes('pdf')
        ? 'pdf'
        : formats[0]
  const eventTriggered = editing?.triggerType === 'event'
  const pipelineDefinition = report?.definition?.reportType === 'pipeline'

  const save = useMutation({
    mutationFn: () => {
      // An event schedule takes its deal from the event that fires it, so it has none of its own to require.
      if (!eventTriggered && templateNeedsLoan(template) && !baseFilters.loan_id) {
        throw new Error('This report needs a deal before it can be scheduled.')
      }
      const parsed = parseTimeInput(time)
      if (!parsed) throw new Error('Enter a time of day as HH:MM.')
      const cadence: Partial<ReportScheduleInput> = eventTriggered
        ? {}
        : {
            frequency,
            dayOfWeek: usesWeekday(frequency) ? weekday : null,
            dayOfMonth: usesDayOfMonth(frequency) ? Math.min(Math.max(Math.trunc(dayOfMonth) || 1, 1), 31) : null,
            hourUtc: parsed.hour,
            minuteLocal: parsed.minute,
            timezone,
            businessDaysOnly: businessDays,
          }
      const common = {
        name: name.trim() || (editing?.name ?? report!.name),
        recipients,
        format,
        deliveryMode,
        senderEmail: deliveryMode === 'none' ? null : (editing?.senderEmail ?? senderEmail),
        ...cadence,
      }
      if (editing) return reportSchedulesService.update(editing.id, scheduleInput(editing, common))
      const payload: ReportScheduleInput = {
        template,
        filters: baseFilters,
        triggerType: 'time',
        triggerEventType: null,
        emailSubject: null,
        emailPreheader: null,
        emailBody: null,
        isActive: false,
        frequency,
        dayOfWeek: usesWeekday(frequency) ? weekday : null,
        dayOfMonth: usesDayOfMonth(frequency) ? Math.min(Math.max(Math.trunc(dayOfMonth) || 1, 1), 31) : null,
        hourUtc: parsed.hour,
        minuteLocal: parsed.minute,
        timezone,
        businessDaysOnly: businessDays,
        ...common,
      }
      return reportSchedulesService.create(payload)
    },
    onSuccess: () =>
      void onSaved(
        editing
          ? editing.isActive
            ? 'Schedule changed and paused. Activate it again to resume.'
            : 'Schedule changed. It is still paused.'
          : 'Schedule saved, paused. Activating it is a separate approval step.',
      ),
    onError: async (cause) => setError(await requestErrorMessage(cause, 'The schedule could not be saved.')),
  })

  return (
    <DialogShell
      label={editing ? `Edit schedule ${editing.name}` : `Schedule ${report!.name}`}
      onClose={onClose}
      width="lg"
      testId="schedule-dialog"
      footer={
        <>
          <Btn variant="quiet" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            busy={save.isPending}
            icon={<CalendarClock className="h-3.5 w-3.5" aria-hidden />}
            onClick={() => {
              setError(null)
              save.mutate()
            }}
          >
            {editing ? 'Save changes' : 'Save schedule'}
          </Btn>
        </>
      }
    >
      <div className="flex max-h-[70vh] flex-col gap-3.5 overflow-auto p-4">
        <SchedulerStatusNotice compact />
        {pipelineDefinition && onOpenStudio ? (
          <div className="rounded-lg border border-[#4A3F1E] bg-[#0E0D09] px-3 py-2 text-[12.5px] text-[#E8D9A8]">
            This saves a schedule for the plain pipeline layout. To schedule this report with its own columns and
            keep the schedule tied to a version of it, use Report Studio.{' '}
            <button type="button" onClick={onOpenStudio} className="font-semibold text-[#FFD86B] underline">
              Open in Report Studio
            </button>
          </div>
        ) : null}

        <Field label="Name" htmlFor="schedule-name">
          <input id="schedule-name" className={INPUT} value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        {eventTriggered ? (
          <p className="rounded-lg border border-[#262626] px-3 py-2 text-[12.5px] text-[#B9B9B9]">
            This schedule runs when an event happens, not on a clock. Only its name, recipients and file can be changed
            here.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Frequency" htmlFor="schedule-frequency">
              <select
                id="schedule-frequency"
                className={INPUT}
                value={frequency}
                onChange={(event) => setFrequency(asFrequency(event.target.value))}
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
            {usesWeekday(frequency) ? (
              <Field label="Day" htmlFor="schedule-weekday">
                <select
                  id="schedule-weekday"
                  className={INPUT}
                  value={String(weekday)}
                  onChange={(event) => setWeekday(Number(event.target.value))}
                >
                  {WEEKDAYS.map((day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {usesDayOfMonth(frequency) ? (
              <Field label="Day of month" htmlFor="schedule-day-of-month" hint="1 to 31. 29, 30 and 31 run on the last day of a shorter month.">
                <input
                  id="schedule-day-of-month"
                  type="number"
                  min={1}
                  max={31}
                  className={INPUT}
                  value={dayOfMonth}
                  onChange={(event) => setDayOfMonth(Number(event.target.value))}
                />
              </Field>
            ) : null}
            <Field label="Time (local)" htmlFor="schedule-time">
              <input
                id="schedule-time"
                type="time"
                step={60}
                className={INPUT}
                value={time}
                onChange={(event) => setTime(event.target.value)}
              />
            </Field>
            <Field label="Time zone" htmlFor="schedule-timezone">
              <select
                id="schedule-timezone"
                className={INPUT}
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
              >
                {[...new Set([...SCHEDULE_TIMEZONES, timezone])].map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-[13px] text-[#D8D8D8] sm:col-span-2">
              <input
                type="checkbox"
                checked={businessDays}
                onChange={(event) => setBusinessDays(event.target.checked)}
                className="h-4 w-4 accent-[#FFD86B]"
              />
              <span>
                Business days only
                <span className="block text-[11.5px] text-[#8C8C8C]">
                  A daily run skips weekends; any other cadence moves a weekend run to the next business day.
                </span>
              </span>
            </label>
          </div>
        )}

        {canChooseFile ? (
          <Field label="File" htmlFor="schedule-file">
            <select
              id="schedule-file"
              className={INPUT}
              value={fileFormat}
              onChange={(event) => setFileFormat(event.target.value === 'xlsx' ? 'xlsx' : 'pdf')}
            >
              <option value="pdf">PDF</option>
              <option value="xlsx">Excel workbook</option>
            </select>
          </Field>
        ) : null}

        <RecipientsEditor value={recipients} onChange={setRecipients} senderEmail={editing?.senderEmail ?? senderEmail} />

        {digestAvailable && deliveryMode !== 'none' ? (
          <Field
            label="Email body"
            htmlFor="schedule-body"
            hint="The PDF is attached and kept either way."
          >
            <select
              id="schedule-body"
              className={INPUT}
              value={asDigest ? 'digest' : 'notice'}
              onChange={(event) => setAsDigest(event.target.value === 'digest')}
            >
              <option value="digest">Digest: the day’s pipeline, in the message</option>
              <option value="notice">Standing notice: one line and the attachment</option>
            </select>
          </Field>
        ) : null}

        {error ? (
          <div role="alert" className="rounded-lg border border-[#4A3220] bg-[#140E0A] px-3 py-2 text-[12.5px] text-[#F0B08A]">
            {error}
          </div>
        ) : null}
        <p className="text-[11.5px] leading-relaxed text-[#8C8C8C]">
          {editing
            ? 'Saving a change pauses the schedule. Nothing is sent until someone with approval activates it again.'
            : 'The schedule is saved paused. Nothing is sent until someone with approval activates it.'}
        </p>
      </div>
    </DialogShell>
  )
}
